import { createSlice, PayloadAction, SliceCaseReducers } from "@reduxjs/toolkit";
import { Job } from "models/Job";
import { WorkNoteType } from "operations/schema/schema";
import { RootState } from "store";

export type PageTypes = Extract<"jobs" | "planner" | "history" | "partner", keyof RootState>;

// Interface for state
export interface State {
  page: PageTypes;
  selectedJobId: string | undefined;
  connectionStatus: boolean;
  forcedOffline: boolean;
  queueLength: number;
}

interface Reducers extends SliceCaseReducers<State> {
  setPage: (state: State, action: PayloadAction<{ page: PageTypes }>) => State;
  setSelectedJob: (state: State, action: PayloadAction<{ jobId: string | undefined }>) => State;
  setConnectionStatus: (state: State, action: PayloadAction<{ status: boolean }>) => State;
  setForcedOffline: (state: State, action: PayloadAction<{ status: boolean }>) => State;
  addQueueLength: (state: State) => State;
  subtractQueueLength: (state: State) => State;
}

interface Selectors {
  isPartnerView: (state: RootState) => boolean;
  selectPageSelectedJob: (state: RootState) => Job;
  selectSelectedJobId: (state: RootState) => string | undefined;
  selectLoadingJob: (state: RootState) => boolean;
  selectConnectionStatus: (state: RootState) => boolean;
  selectForcedOffline: (state: RootState) => boolean;
  selectPageWorkNotes: (state: RootState) => WorkNoteType[];
  selectQueueLength: (state: RootState) => number;
}

export const initialState: State = {
  page: "jobs",
  selectedJobId: undefined,
  connectionStatus: true,
  forcedOffline: false,
  queueLength: 0,
};

const reducers: Reducers = {
  setPage(state, { payload: { page } }) {
    state.page = page;
    return state;
  },
  setSelectedJob: (state, { payload: { jobId } }) => {
    state.selectedJobId = jobId;
    return state;
  },
  setConnectionStatus: (state, { payload: { status } }) => {
    state.connectionStatus = status;
    return state;
  },
  setForcedOffline: (state, { payload: { status } }) => {
    state.forcedOffline = status;
    return state;
  },
  addQueueLength(state) {
    state.queueLength += 1;
    return state;
  },
  subtractQueueLength(state) {
    state.queueLength -= 1;
    if (state.queueLength < 0) state.queueLength = 0;
    return state;
  },
};

const selectors: Selectors = {
  isPartnerView(state) {
    return state.root.page === "partner";
  },
  selectPageSelectedJob(state) {
    let {
      root: { selectedJobId, page },
    } = state;
    if (selectedJobId && state[page].jobs[selectedJobId]) {
      return state[page].jobs[selectedJobId];
    }
    throw new Error("SelectedJob unavailable. Should not be possible to trigger");
  },
  selectSelectedJobId({ root: { selectedJobId } }) {
    return selectedJobId;
  },
  selectLoadingJob(state) {
    let {
      root: { page },
    } = state;
    return !!state[page].loadingJob;
  },
  selectConnectionStatus({ root: { connectionStatus, forcedOffline } }) {
    return connectionStatus && !forcedOffline;
  },
  selectForcedOffline({ root: { forcedOffline } }) {
    return forcedOffline;
  },
  selectPageWorkNotes(state) {
    let {
      root: { selectedJobId, page },
    } = state;
    if (selectedJobId && state[page].jobs[selectedJobId]) {
      return state[page].jobs[selectedJobId].workNotes;
    }
    throw new Error("SelectedJob unavailable. Should not be possible to trigger");
  },
  selectQueueLength(state) {
    return state.root.queueLength;
  },
};

const storeBase = createSlice<State, Reducers>({
  name: "root",
  initialState,
  reducers,
});

// To be imported and added in store/reducers
export default storeBase.reducer;

export const {
  setPage,
  setSelectedJob,
  setConnectionStatus,
  setForcedOffline,
  addQueueLength,
  subtractQueueLength,
} = storeBase.actions;

export const {
  isPartnerView,
  selectPageSelectedJob,
  selectSelectedJobId,
  selectLoadingJob,
  selectConnectionStatus,
  selectForcedOffline,
  selectPageWorkNotes,
  selectQueueLength,
} = selectors;
